import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../App.css';
import { PhotoFile } from '../hooks/usePhotoHandler';

const MAX_PHOTOS = 3;

function PreviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const photos = useMemo(() => (location.state as { photos?: PhotoFile[] })?.photos || [], [location.state]);

  useEffect(() => {
    if (photos.length === 0) {
      navigate('/');
    }
  }, [photos, navigate]);

  const handleSubmit = () => {
    // Navigate to loading page where the API call will be made
    navigate('/loading', { state: { photos } });
  };

  const handleBack = () => {
    navigate('/');
  };

  if (photos.length === 0) {
    return (
      <div className="main-content">
        <p>No photos selected. Please go back and upload photos.</p>
        <span className="back-link" onClick={handleBack}>
          Go Back
        </span>
      </div>
    );
  }

  return (
    <div className="main-content">
      {/* Step indicator */}
      <div className="preview-step-indicator">
        <div className="step-indicator">
          <div className="step completed">
            <div className="step-number">1</div>
            <div className="step-label">Upload</div>
          </div>
          <div className="step-connector"></div>
          <div className="step active">
            <div className="step-number">2</div>
            <div className="step-label">Preview</div>
          </div>
        </div>
      </div>

      <h1 className="main-title preview-title">Preview Your Photos</h1>
      
      <div className="preview-description">
        <p>Review your photos below before submitting your report. Make sure the license plate is clear and readable.</p>
      </div>
      
      <div className="photo-preview-container">
        <div className="photo-preview-grid preview-mode">
          {photos.map((photo, index) => (
            <div 
              key={photo.id} 
              className={`photo-preview-item preview-item ${currentPhotoIndex === index ? 'active' : ''}`}
              onClick={() => setCurrentPhotoIndex(index)}
            >
              <img src={photo.url} alt={`License plate ${index + 1}`} />
              <div className="photo-preview-overlay">
                <div className="photo-number">{index + 1}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="preview-photo-info">
          <p className="photo-count preview-count">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M1 8a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.07 3h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 6H17a2 2 0 012 2v7a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm13.5 3a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM10 14a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            {photos.length} of {MAX_PHOTOS} photos ready to submit
          </p>
        </div>
      </div>

      <div className="preview-actions">
        <button className="action-btn primary-submit-btn" onClick={handleSubmit}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.896 28.896 0 003.105 2.289z" />
          </svg>
          Submit Report
        </button>

        <span className="back-link preview-back" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
          Back to Edit Photos
        </span>
      </div>
    </div>
  );
}

export default PreviewPage;
